import { MouseEvent, forwardRef } from "react";
import { Button, ButtonProps } from "../Button";
import { useDrillDownContext } from "./internal/useDrillDownContext";
import { DataTrackingId } from "../../types";
import { useTrackingId } from "../../hooks";

/**
 * Props for the DrillDownPrevButton component.
 *
 * @extends ButtonProps
 * @extends DataTrackingId
 */
export type DrillDownPrevButtonProps = ButtonProps & DataTrackingId;

/**
 * Button that navigates to the previous drilldown screen.
 *
 * Decrements the drilldown index by one when clicked. Automatically integrates with the
 * DrillDown context for navigation. Preserves custom onClick handlers.
 *
 * Features:
 * - Automatic navigation to previous index
 * - Automatic analytics tracking via data-tracking-id
 * - All Button component props supported
 * - Default accessible label for screen readers
 *
 * @example
 * ```tsx
 * <DrillDown.PrevButton>Back</DrillDown.PrevButton>
 * <DrillDown.PrevButton onClick={logBackAction}>Go Back</DrillDown.PrevButton>
 * ```
 */
export const DrillDownPrevButton = forwardRef<
  HTMLButtonElement,
  DrillDownPrevButtonProps
>((props, ref) => {
  const { onClick, ...rest } = props;
  const { back } = useDrillDownContext();

  const data = {
    title: props.title,
    appearance: props.appearance,
    size: props.size,
    type: props.type,
  };

  const trackingId = useTrackingId({
    name: "DrillDownPrevButton",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    back?.();
  };

  return (
    <Button
      aria-label="Prev"
      data-anv="drilldown-prev-button"
      data-tracking-id={trackingId}
      onClick={onClickHandler}
      ref={ref}
      {...rest}
    />
  );
});

DrillDownPrevButton.displayName = "DrillDownPrevButton";
